// Admin SavWar Yönetim Paneli
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import { ALL_RANKS } from '../lib/ranks';

interface SavWarConfig {
  tpMultiplier: number;
  customRankThresholds: { [level: number]: number } | null;
  updatedAt: string;
  updatedBy?: string;
}

interface ActiveSession {
  id: string;
  topic: string;
  startTime: string;
  endTime: string;
}

interface RankInfo {
  level: number;
  name: string;
  minTP: number;
  customMinTP: number | null;
  effectiveMinTP: number;
}

export default function AdminSavWarPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<SavWarConfig | null>(null);
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [ranks, setRanks] = useState<RankInfo[]>([]);
  
  // TP Multiplier state
  const [tpMultiplier, setTpMultiplier] = useState<string>('1.0');
  const [updatingMultiplier, setUpdatingMultiplier] = useState(false);
  
  // Rank Thresholds state
  const [customThresholds, setCustomThresholds] = useState<{ [level: number]: string }>({});
  const [updatingThresholds, setUpdatingThresholds] = useState(false);
  
  // Session Management state
  const [newTopicTag, setNewTopicTag] = useState('');
  const [durationHours, setDurationHours] = useState<string>('24');
  const [creatingSession, setCreatingSession] = useState(false);
  const [closingSession, setClosingSession] = useState(false);
  
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: any = await apiGet('/api/admin/savwar/config');
      
      if (response && response.ok) {
        setConfig(response.config);
        setActiveSession(response.activeSession);
        setRanks(response.rankSystem.ranks || []);
        setTpMultiplier(String(response.config.tpMultiplier));
        
        // Custom thresholds'u string map'e çevir
        const thresholdsMap: { [level: number]: string } = {};
        if (response.config.customRankThresholds) {
          Object.entries(response.config.customRankThresholds).forEach(([level, value]) => {
            thresholdsMap[parseInt(level)] = String(value);
          });
        }
        setCustomThresholds(thresholdsMap);
      }
    } catch (err: any) {
      console.error('[AdminSavWar] Load error:', err);
      setError('Yapılandırma yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMultiplier = async (recalculate: boolean = true) => {
    const multiplierValue = parseFloat(tpMultiplier);
    if (isNaN(multiplierValue) || multiplierValue < 0.1 || multiplierValue > 10.0) {
      alert('TP çarpanı 0.1 ile 10.0 arasında olmalıdır');
      return;
    }

    try {
      setUpdatingMultiplier(true);
      setError(null);
      setSuccessMessage(null);
      
      const response: any = await apiPatch('/api/admin/savwar/config/tp-multiplier', {
        tpMultiplier: multiplierValue,
        recalculateRanks: recalculate,
      });

      if (response && response.ok) {
        setSuccessMessage(response.message || 'TP çarpanı güncellendi');
        await loadConfig();
      }
    } catch (err: any) {
      const msg = err.data?.error || err.message || 'TP çarpanı güncellenemedi';
      setError(msg);
    } finally {
      setUpdatingMultiplier(false);
    }
  };

  const handleUpdateThresholds = async () => {
    try {
      setUpdatingThresholds(true);
      setError(null);
      setSuccessMessage(null);
      
      // String'leri number'a çevir
      const thresholds: { [level: number]: number } = {};
      Object.entries(customThresholds).forEach(([level, value]) => {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 0) {
          thresholds[parseInt(level)] = numValue;
        }
      });

      if (Object.keys(thresholds).length === 0) {
        alert('En az bir rütbe eşiği girilmelidir');
        return;
      }

      const response: any = await apiPatch('/api/admin/savwar/config/rank-thresholds', {
        thresholds,
        recalculateRanks: true,
      });

      if (response && response.ok) {
        setSuccessMessage(response.message || 'Rütbe eşikleri güncellendi');
        await loadConfig();
      }
    } catch (err: any) {
      const msg = err.data?.error || err.message || 'Rütbe eşikleri güncellenemedi';
      setError(msg);
    } finally {
      setUpdatingThresholds(false);
    }
  };

  const handleResetThresholds = async () => {
    if (!confirm('Tüm özel rütbe eşiklerini sıfırlamak istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    try {
      setUpdatingThresholds(true);
      setError(null);
      setSuccessMessage(null);
      
      const response: any = await apiDelete('/api/admin/savwar/config/rank-thresholds');

      if (response && response.ok) {
        setSuccessMessage(response.message || 'Rütbe eşikleri varsayılana döndürüldü');
        setCustomThresholds({});
        await loadConfig();
      }
    } catch (err: any) {
      const msg = err.data?.error || err.message || 'Rütbe eşikleri sıfırlanamadı';
      setError(msg);
    } finally {
      setUpdatingThresholds(false);
    }
  };

  const handleCreateSession = async () => {
    if (!newTopicTag.trim()) {
      alert('Konu etiketi girilmelidir');
      return;
    }

    const duration = parseInt(durationHours);
    if (isNaN(duration) || duration < 1 || duration > 168) {
      alert('Süre 1 ile 168 saat arasında olmalıdır');
      return;
    }

    try {
      setCreatingSession(true);
      setError(null);
      setSuccessMessage(null);
      
      const response: any = await apiPost('/api/admin/savwar/sessions', {
        topicTag: newTopicTag.trim(),
        durationHours: duration,
      });

      if (response && response.success) {
        setSuccessMessage(`SavWar başlatıldı: ${newTopicTag} (${duration}h)`);
        setNewTopicTag('');
        setDurationHours('24');
        await loadConfig();
      }
    } catch (err: any) {
      const errorData = err.data || err.response?.data || {};
      const msg = errorData.error || err.message || 'Seans başlatılamadı';
      const code = errorData.code;
      
      if (code === 'SESSION_ALREADY_ACTIVE') {
        setError('Bu konu için zaten aktif bir oturum var');
      } else {
        setError(msg);
      }
    } finally {
      setCreatingSession(false);
    }
  };

  const handleCloseSession = async () => {
    if (!activeSession) return;
    if (!confirm('Aktif seansı şimdi kapatmak istediğinize emin misiniz?')) return;

    try {
      setClosingSession(true);
      setError(null);
      setSuccessMessage(null);
      
      const response: any = await apiPost(`/api/admin/savwar/sessions/${activeSession.id}/close`, {});

      if (response && response.success) {
        setSuccessMessage('Seans kapatıldı ve sonuçlar yayınlandı');
        await loadConfig();
      }
    } catch (err: any) {
      const msg = err.data?.error || err.message || 'Seans kapatılamadı';
      setError(msg);
    } finally {
      setClosingSession(false);
    }
  };

  const calculateEffectPreview = () => {
    const multiplier = parseFloat(tpMultiplier) || 1.0;
    const tpPerStar = 1 * multiplier;
    const tpPerComment = 0.2 * multiplier;
    return { tpPerStar, tpPerComment };
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div>⏳ Yükleniyor...</div>
      </div>
    );
  }

  const effectPreview = calculateEffectPreview();

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2>⚙️ SavWar Yönetimi</h2>
        <button onClick={() => navigate('/savwar')}>← SavWar'a Dön</button>
      </div>

      {error && (
        <div style={{ padding: 16, marginBottom: 16, background: '#3a0000', border: '1px solid #8b0000', borderRadius: 8, color: '#ff6b6b' }}>
          ❌ {error}
        </div>
      )}

      {successMessage && (
        <div style={{ padding: 16, marginBottom: 16, background: '#0a2a0a', border: '1px solid #2a8b2a', borderRadius: 8, color: '#6bff6b' }}>
          ✅ {successMessage}
        </div>
      )}

      {/* Aktif Seans Bilgisi */}
      <div style={{ padding: 20, border: '1px solid #2c2c2c', borderRadius: 8, marginBottom: 24, background: '#0a0a0a' }}>
        <h3 style={{ marginBottom: 16 }}>📊 Aktif Seans</h3>
        {activeSession ? (
          <div>
            <div style={{ marginBottom: 12 }}>
              <strong>Konu:</strong> {activeSession.topic}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Başlangıç:</strong> {new Date(activeSession.startTime).toLocaleString()}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Bitiş:</strong> {new Date(activeSession.endTime).toLocaleString()}
            </div>
            <button
              onClick={handleCloseSession}
              disabled={closingSession}
              style={{ background: '#8b0000', padding: '8px 16px', borderRadius: 6 }}
            >
              {closingSession ? 'Kapatılıyor...' : '🛑 Seansı Şimdi Kapat'}
            </button>
          </div>
        ) : (
          <div style={{ opacity: 0.7 }}>Aktif seans yok</div>
        )}
      </div>

      {/* Seans Başlatma */}
      <div style={{ padding: 20, border: '1px solid #2c2c2c', borderRadius: 8, marginBottom: 24, background: '#0a0a0a' }}>
        <h3 style={{ marginBottom: 16 }}>🚀 Yeni Seans Başlat</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 14 }}>
              Konu Etiketi (# ile başlamalı)
            </label>
            <input
              type="text"
              value={newTopicTag}
              onChange={(e) => setNewTopicTag(e.target.value)}
              placeholder="#Teknoloji"
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #2c2c2c',
                background: '#0f0f0f',
                color: 'inherit',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 14 }}>
              Süre (Saat, 1-168)
            </label>
            <input
              type="number"
              value={durationHours}
              onChange={(e) => setDurationHours(e.target.value)}
              min="1"
              max="168"
              style={{
                width: '100%',
                maxWidth: 200,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #2c2c2c',
                background: '#0f0f0f',
                color: 'inherit',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <button
            onClick={handleCreateSession}
            disabled={creatingSession || !newTopicTag.trim()}
            style={{
              padding: '10px 20px',
              borderRadius: 6,
              background: '#2a8b2a',
              border: '1px solid #3a9a3a',
              color: '#fff',
              cursor: creatingSession || !newTopicTag.trim() ? 'not-allowed' : 'pointer',
              maxWidth: 200,
            }}
          >
            {creatingSession ? 'Başlatılıyor...' : '🎯 Seans Başlat'}
          </button>
        </div>
      </div>

      {/* TP Çarpanı */}
      <div style={{ padding: 20, border: '1px solid #2c2c2c', borderRadius: 8, marginBottom: 24, background: '#0a0a0a' }}>
        <h3 style={{ marginBottom: 16 }}>⭐ TP Çarpanı</h3>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 12 }}>
            TP kazanımını ayarlayın. Varsayılan: 1.0x (1 yıldız = 1 TP, 1 yorum = 0.2 TP)
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {[0.5, 1.0, 1.5, 2.0, 3.0, 5.0].map((preset) => (
              <button
                key={preset}
                onClick={() => setTpMultiplier(String(preset))}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  background: parseFloat(tpMultiplier) === preset ? '#2a8b2a' : 'transparent',
                  border: '1px solid #2c2c2c',
                }}
              >
                {preset}x
              </button>
            ))}
          </div>
          <input
            type="number"
            value={tpMultiplier}
            onChange={(e) => setTpMultiplier(e.target.value)}
            min="0.1"
            max="10.0"
            step="0.1"
            style={{
              width: '100%',
              maxWidth: 200,
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid #2c2c2c',
              background: '#0f0f0f',
              color: 'inherit',
              fontFamily: 'inherit',
              marginBottom: 12,
            }}
          />
          <div style={{ padding: 12, background: '#0f0f0f', borderRadius: 6, marginBottom: 12 }}>
            <strong>Etki Önizlemesi:</strong>
            <div style={{ marginTop: 8, fontSize: 14 }}>
              • 1 Yıldız = <strong>{effectPreview.tpPerStar.toFixed(1)} TP</strong>
            </div>
            <div style={{ fontSize: 14 }}>
              • 1 Yorum = <strong>{effectPreview.tpPerComment.toFixed(2)} TP</strong>
            </div>
          </div>
          <button
            onClick={() => handleUpdateMultiplier(true)}
            disabled={updatingMultiplier}
            style={{
              padding: '10px 20px',
              borderRadius: 6,
              background: '#2a8b2a',
              border: '1px solid #3a9a3a',
              color: '#fff',
              cursor: updatingMultiplier ? 'not-allowed' : 'pointer',
              marginRight: 8,
            }}
          >
            {updatingMultiplier ? 'Güncelleniyor...' : '💾 Kaydet & Tüm Rütbeleri Yeniden Hesapla'}
          </button>
        </div>
      </div>

      {/* Rütbe Eşikleri */}
      <div style={{ padding: 20, border: '1px solid #2c2c2c', borderRadius: 8, marginBottom: 24, background: '#0a0a0a' }}>
        <h3 style={{ marginBottom: 16 }}>🏅 Rütbe Eşikleri</h3>
        <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 16 }}>
          Her rütbe seviyesi için özel TP eşiği belirleyin. Boş bırakırsanız varsayılan değer kullanılır.
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12, marginBottom: 16 }}>
          {ranks.map((rank) => {
            const customValue = customThresholds[rank.level] || '';
            const hasCustom = !!customValue;
            
            return (
              <div
                key={rank.level}
                style={{
                  padding: 12,
                  border: '1px solid #2c2c2c',
                  borderRadius: 6,
                  background: hasCustom ? '#1a1a00' : '#0f0f0f',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  #{rank.level} - {rank.name}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                  Varsayılan: {rank.minTP} TP
                </div>
                <input
                  type="number"
                  value={customValue}
                  onChange={(e) => {
                    setCustomThresholds(prev => ({
                      ...prev,
                      [rank.level]: e.target.value,
                    }));
                  }}
                  placeholder={String(rank.minTP)}
                  min="0"
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    borderRadius: 4,
                    border: '1px solid #2c2c2c',
                    background: '#0a0a0a',
                    color: 'inherit',
                    fontFamily: 'inherit',
                  }}
                />
                {hasCustom && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#ffd700' }}>
                    ✨ Özel: {customValue} TP
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleUpdateThresholds}
            disabled={updatingThresholds}
            style={{
              padding: '10px 20px',
              borderRadius: 6,
              background: '#2a8b2a',
              border: '1px solid #3a9a3a',
              color: '#fff',
              cursor: updatingThresholds ? 'not-allowed' : 'pointer',
            }}
          >
            {updatingThresholds ? 'Güncelleniyor...' : '💾 Eşikleri Kaydet & Rütbeleri Yeniden Hesapla'}
          </button>
          <button
            onClick={handleResetThresholds}
            disabled={updatingThresholds}
            style={{
              padding: '10px 20px',
              borderRadius: 6,
              background: '#8b0000',
              border: '1px solid #a00000',
              color: '#fff',
              cursor: updatingThresholds ? 'not-allowed' : 'pointer',
            }}
          >
            {updatingThresholds ? 'Sıfırlanıyor...' : '🔄 Varsayılana Döndür'}
          </button>
        </div>
      </div>

      {/* Son Güncelleme Bilgisi */}
      {config && (
        <div style={{ padding: 16, border: '1px solid #2c2c2c', borderRadius: 8, background: '#0a0a0a', opacity: 0.7, fontSize: 14 }}>
          <strong>Son Güncelleme:</strong> {new Date(config.updatedAt).toLocaleString()}
          {config.updatedBy && <> | <strong>Güncelleyen:</strong> {config.updatedBy}</>}
        </div>
      )}
    </div>
  );
}
